import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { EvaluationQuestionTypeIds, IUserEvaluationAnswerDto, IUserEvaluationDto, IUserEvaluationQuestionDto } from 'src/app/contracts';
import { UI } from 'src/app/core/helpers/ui';
import { customerEffortEvaluations, EvaluationData } from 'src/app/data/evaluation.data';
import { BasePopover } from 'src/app/shared/components/base-popover/base.popover';

@Component({
  templateUrl: 'evaluation.popover.html',
  styleUrls: ['evaluation.popover.scss'],
  imports: [
    IonTextarea,
    FormsModule,
    IonLabel,
    IonItem,
    IonButtons,
    IonToolbar,
    IonHeader,
    CommonModule,
    FormsModule,
    TranslatePipe,
    IonContent,
    IonFooter,
    IonRow,
    IonCol,
    IonIcon,
    IonButton,
    IonSelect,
    IonSelectOption,
  ],
})
export class EvaluationPopoverComponent extends BasePopover {
  private ui = inject(UI);
  private data = inject(EvaluationData);
  EvaluationQuestionTypeIds = EvaluationQuestionTypeIds;
  @Input({ required: true }) evaluation!: IUserEvaluationDto;
  @Input() readOnly = false;
  override ngOnInit(): void {
    super.ngOnInit();
    if (this.readOnly) {
      this.populateAnswers();
      if (customerEffortEvaluations.includes(this.evaluation.evaluatedItemTypeId)) {
        // Show/hide the text field based on selected choice without resetting its value
        const choicesQuestion = this.evaluation.questions.find(q => q.type === EvaluationQuestionTypeIds.Choices);
        const choiceIndex = choicesQuestion ? choicesQuestion.options.findIndex(o => o.id === choicesQuestion.value) : -1;
        const reasonTextQuestion = this.evaluation.questions.find(i => i.type === EvaluationQuestionTypeIds.Text && i.isOptional);
        if (reasonTextQuestion) {
          reasonTextQuestion.hidden = choiceIndex < 0 || choiceIndex >= 3;
        }
      }
    } else if (customerEffortEvaluations.includes(this.evaluation.evaluatedItemTypeId)) {
      this.toggleCustomerEffortReasonTextField();
    }
  }
  canSubmit() {
    if (this.readOnly) return false;
    const answeredRequiredQuestions = this.evaluation.questions.every(i => i.isOptional || !!i.value?.toString().trim());
    if (answeredRequiredQuestions) {
      for (const question of this.evaluation.questions) {
        if (question.isOtherOptionSelected && !question.otherOptionValue?.trim()) {
          return false;
        }
      }
      if (customerEffortEvaluations.includes(this.evaluation.evaluatedItemTypeId)) {
        const reasonTextQuestion = this.evaluation.questions.find(i => i.type === EvaluationQuestionTypeIds.Text && i.isOptional);
        if (reasonTextQuestion && !reasonTextQuestion.hidden && !reasonTextQuestion.value?.toString().trim()) {
          return false;
        }
      }
    }
    return answeredRequiredQuestions;
  }
  submit() {
    if (this.readOnly || !this.canSubmit()) {
      return;
    }
    const answers: IUserEvaluationAnswerDto[] = this.evaluation.questions.map(i => ({
      id: i.id,
      answer: i.value?.toString().trim() || '',
      otherOptionValue: i.otherOptionValue?.trim() || undefined,
    }));
    this.data.submit({ userEvaluationId: this.evaluation.id, answers }).subscribe(() => {
      this.ui.showToast(this.translation.Evaluation.ThankYouWeAppreciateYourTimeAndEffort);
      this.close(true);
    });
  }
  onDropdownQuestionChanged(question: IUserEvaluationQuestionDto) {
    const selectedOption = question.options.find(opt => opt.id === question.value);
    question.isOtherOptionSelected = selectedOption?.isOther;
  }
  selectChoice(question: IUserEvaluationQuestionDto, choiceIndex: number) {
    const selectedOption = question.options[choiceIndex];
    question.value = selectedOption.id;
    question.isOtherOptionSelected = selectedOption?.isOther;
    if (customerEffortEvaluations.includes(this.evaluation.evaluatedItemTypeId)) {
      this.toggleCustomerEffortReasonTextField(choiceIndex);
    }
  }
  toggleCustomerEffortReasonTextField(choiceIndex?: number) {
    const reasonTextQuestion = this.evaluation.questions.find(i => i.type === EvaluationQuestionTypeIds.Text && i.isOptional);
    if (reasonTextQuestion) {
      reasonTextQuestion.hidden = choiceIndex == undefined || choiceIndex >= 3;
      reasonTextQuestion.value = null;
    }
  }
  private populateAnswers() {
    for (const question of this.evaluation.questions) {
      if (!question.answer) continue;
      switch (question.type) {
        case EvaluationQuestionTypeIds.Stars:
          question.value = +question.answer;
          break;
        case EvaluationQuestionTypeIds.NetPromoterScore:
          question.value = +question.answer;
          break;
        case EvaluationQuestionTypeIds.Dropdown:
        case EvaluationQuestionTypeIds.Choices: {
          question.value = +question.answer;
          const selectedOption = question.options.find(o => o.id === +question.answer);
          if (selectedOption?.isOther && question.otherOptionValue) {
            question.isOtherOptionSelected = true;
          }
          break;
        }
        default:
          question.value = question.answer;
          break;
      }
    }
  }
}
